(() => {
  'use strict';
  if (window.avonticTeamOrders) return;
  window.avonticTeamOrders = true;

  const TEAM_AT = 5;
  const QUOTE_AT = 10;
  const ROLLOUT_AT = 100;
  const unitCache = new WeakMap();
  const defaults = new WeakMap();
  const announced = new Set();

  const emit = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
    document.dispatchEvent(new CustomEvent(`avontic:${name}`, { detail }));
  };

  const esc = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"'));
  const number = (value) => new Intl.NumberFormat(document.documentElement.lang || 'en-US').format(value);

  // form.id can be shadowed by an <input name="id">, so read the attribute directly.
  const formIdOf = (node) => node.closest('form')?.getAttribute('id') || '';

  const quantityInput = (formId) => {
    if (!formId) return null;
    return document.querySelector(`input[name="quantity"][form="${esc(formId)}"]`) ||
      document.getElementById(formId)?.querySelector('input[name="quantity"]') || null;
  };

  const variantId = (formId) => {
    if (!formId) return '';
    const form = document.getElementById(formId);
    const candidates = [
      ...document.querySelectorAll(`[name="id"][form="${esc(formId)}"]`),
      ...(form ? form.querySelectorAll('[name="id"]') : [])
    ];
    const live = candidates.find((input) => !input.disabled && input.value);
    return live ? String(live.value) : '';
  };

  const unitsPerItem = (strip, id) => {
    if (!unitCache.has(strip)) {
      let map = null;
      try { map = JSON.parse(strip.querySelector('[data-avt-units]')?.textContent || 'null'); } catch (_) { map = null; }
      unitCache.set(strip, map);
    }
    const map = unitCache.get(strip);
    if (map && id && map[id]) return Number(map[id]) || 1;
    if (map) return 0;
    return 1;
  };

  const quoteHref = (strip, units, id) => {
    const url = new URL(strip.dataset.quoteBase || '/pages/business-solutions', window.location.origin);
    if (units > 0) url.searchParams.set('quantity', String(units));
    const formId = strip.dataset.formId || formIdOf(strip);
    if (id) url.searchParams.set('pack_count', String(Math.max(1, parseInt(quantityInput(formId)?.value, 10) || 1)));
    const params = new URLSearchParams(location.search);
    ['preview_theme_id', '_fd', 'pb', 'currency', 'country', 'locale', 'model', 'smartfit_model'].forEach(key => {
      if (params.has(key)) url.searchParams.set(key, params.get(key));
    });
    if (id) url.searchParams.set('variant', id);
    url.hash = 'avb-quote';
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const syncCard = (productId, units) => {
    const tier = units >= ROLLOUT_AT ? '100' : units >= QUOTE_AT ? '10' : '1';
    document.querySelectorAll(`[data-avt-card="${esc(productId)}"]`).forEach((card) => {
      card.querySelectorAll('[data-avt-tier]').forEach((item) => {
        const active = item.dataset.avtTier === tier;
        item.classList.toggle('is-active', active);
        if (active) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      const cta = card.querySelector('[data-avt-card-cta]');
      const strip = document.querySelector(`[data-avt-strip][data-product-id="${esc(productId)}"]`);
      if (cta && strip) cta.href = quoteHref(strip, units, variantId(strip.dataset.formId || formIdOf(strip)));
    });
  };

  const update = (strip) => {
    if (!strip.isConnected) return;
    const title = strip.querySelector('[data-avt-title]');
    const sub = strip.querySelector('[data-avt-sub]');
    const status = strip.querySelector('[data-avt-status]');
    const cta = strip.querySelector('[data-avt-cta]');
    const ctaLabel = strip.querySelector('[data-avt-cta-label]');
    if (!title || !sub || !cta || !ctaLabel) return;

    if (!defaults.has(strip)) {
      defaults.set(strip, { title: title.textContent.trim(), sub: sub.textContent.trim(), cta: ctaLabel.textContent.trim() });
    }
    const base = defaults.get(strip);
    if (!strip.dataset.formId) strip.dataset.formId = formIdOf(strip);
    const formId = strip.dataset.formId;
    const quantity = Math.max(1, parseInt(quantityInput(formId)?.value, 10) || 1);
    const id = variantId(formId);
    const units = id ? quantity * unitsPerItem(strip, id) : 0;
    const noun = units === 1 ? (strip.dataset.nounOne || 'case') : (strip.dataset.noun || 'cases');
    const hasPacks = strip.dataset.hasPacks === 'true';
    const isTeamProduct = strip.dataset.kind === 'team';
    const state = units >= QUOTE_AT ? 'quote' : units >= TEAM_AT ? 'team' : 'default';

    if (state === 'quote') {
      title.textContent = `Ordering ${number(units)} ${noun}? Get a formal quote.`;
      sub.textContent = units >= ROLLOUT_AT
        ? 'Volume pricing may be available. We confirm fit for every model and a bulk shipping timeline with your quote.'
        : 'Volume pricing may be available at this quantity. PO, W-9 and tax-exempt support available.';
      ctaLabel.textContent = `Quote for ${number(units)}`;
      status.textContent = isTeamProduct ? `${number(quantity)} ${quantity === 1 ? 'pack' : 'packs'} × ${number(units / quantity)} cases` : '';
    } else if (state === 'team') {
      title.textContent = `Buying ${number(units)} ${noun}?`;
      sub.textContent = isTeamProduct
        ? 'Mixing models or colors? One formal quote can cover every pack.'
        : hasPacks
          ? 'Ready-made team packs are below — or get one quote for mixed models and colors.'
          : 'We can confirm every model and color and prepare one quote for your team.';
      ctaLabel.textContent = base.cta;
      status.textContent = isTeamProduct ? `${number(quantity)} ${quantity === 1 ? 'pack' : 'packs'} × ${number(units / quantity)} cases` : '';
    } else {
      title.textContent = base.title;
      sub.textContent = base.sub;
      ctaLabel.textContent = base.cta;
      status.textContent = isTeamProduct && units ? `${number(quantity)} ${quantity === 1 ? 'pack' : 'packs'} × ${number(units / quantity)} cases = ${number(units)} devices` : '';
    }

    if (!id) {
      status.textContent = JSON.parse(document.querySelector('[data-avt-messages]')?.textContent || '{}').invalid_selection || '';
    }
    cta.href = quoteHref(strip, units, id);
    cta.dataset.avtUnits = String(units);

    const more = strip.querySelector('[data-avt-more]');
    if (more) {
      const anchor = more.getAttribute('href') || '';
      const present = anchor.startsWith('#') && anchor.length > 1 && document.getElementById(anchor.slice(1));
      more.style.display = present ? '' : 'none';
    }

    if (strip.dataset.state !== state) {
      const previous = strip.dataset.state;
      strip.dataset.state = state;
      if (previous && state !== 'default') {
        strip.classList.remove('is-pulse');
        void strip.offsetWidth;
        strip.classList.add('is-pulse');
        const key = `${strip.dataset.productHandle}:${state}`;
        if (!announced.has(key)) {
          announced.add(key);
          emit('av_team_nudge_shown', { state, units, product_handle: strip.dataset.productHandle });
        }
      }
    }

    syncCard(strip.dataset.productId, units);
  };

  let frame = 0;
  const refresh = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      document.querySelectorAll('[data-avt-strip]').forEach(update);
    });
  };

  const settle = () => {
    refresh();
    window.setTimeout(refresh, 90);
    window.setTimeout(refresh, 320);
  };

  document.addEventListener('input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') refresh();
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('input[name="quantity"], [name="id"]') || target.closest('variant-picker, product-variants, form[action*="/cart/add"]')) settle();
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('.quantity-selector__button, quantity-selector button')) settle();

    const tracked = target.closest('[data-avt-track]');
    if (tracked) {
      const strip = document.querySelector('[data-avt-strip]');
      emit('av_team_order_click', {
        placement: tracked.dataset.avtTrack,
        units: Number(strip?.querySelector('[data-avt-cta]')?.dataset.avtUnits || 1),
        product_handle: strip?.dataset.productHandle || ''
      });
    }

    const more = target.closest('[data-avt-more]');
    if (more) {
      const card = document.querySelector(more.getAttribute('href'));
      if (card) {
        event.preventDefault();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        card.setAttribute('tabindex', '-1');
        card.focus({ preventScroll: true });
      }
    }
  });

  ['variant:change', 'product:variant-change', 'variant:changed', 'shopify:section:load'].forEach((name) => document.addEventListener(name, settle));

  new MutationObserver((records) => {
    const relevant = records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (
      node.matches('[data-avt-strip], [data-avt-card], quantity-selector') ||
      node.querySelector('[data-avt-strip], [data-avt-card], quantity-selector')
    )));
    if (relevant) settle();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', settle, { once: true });
  else settle();
})();

